import type { Contato, RegimeTributario, RegistroBase, Representada } from '../db/tipos'
import { converterNumeroBR } from '../lib/textoBR'
import type { CnpjEncontrado } from './buscarCnpj'
import type { CepEncontrado } from './buscarCep'

// O cadastro da fornecedora como UM valor (revisao de 24/09 da spec): com um
// Salvar por aba, a tela precisa responder "esta aba mudou?" e descartar
// voltando ao que esta salvo -- as duas coisas sao comparar dois valores.

export type AbaRepresentada = 'identificacao' | 'localizacao' | 'comercial'

const ABAS_VALIDAS: readonly AbaRepresentada[] = ['identificacao', 'localizacao', 'comercial']

function ehAbaRepresentada(v: string): v is AbaRepresentada {
  return (ABAS_VALIDAS as readonly string[]).includes(v)
}

// Task 16 (escolha dela em 25/09, "2"): voltar de uma tabela de preco, dos
// percentuais por produto/linha ou pela seta do cabecalho reabre a
// fornecedora na Politica Comercial -- a URL carrega `?aba=comercial`. So
// aceita os 3 valores conhecidos; qualquer outra coisa (link antigo, digitado
// a mao) cai em identificacao, a aba de sempre.
export function abaDaUrl(valor: string | null): AbaRepresentada {
  return valor !== null && ehAbaRepresentada(valor) ? valor : 'identificacao'
}

export interface FormularioRepresentada {
  nome: string
  razaoSocial: string
  cnpj: string
  inscricaoEstadual: string
  inscricaoMunicipal: string
  regimeTributario: string
  segmento: string
  prazoMedio: string
  transportadoras: string
  observacoes: string
  calculaImposto: boolean
  ufOrigem: string
  cep: string
  endereco: string
  numero: string
  complemento: string
  bairro: string
  municipio: string
  uf: string
  email: string
  ddd: string
  telefone: string
  nomeContato: string
  whatsapp: string
  cargo: string
  website: string
  instagram: string
  pedidoMinimo: string
  formasAtivas: string[]
  formasExtras: string[]
  limiteSolicitado: string
  limiteAprovado: string
  fretePadrao: string
}

export const CAMPOS_DA_ABA: Record<AbaRepresentada, ReadonlyArray<keyof FormularioRepresentada>> = {
  identificacao: [
    'nome', 'razaoSocial', 'cnpj', 'inscricaoEstadual', 'inscricaoMunicipal', 'regimeTributario',
    'segmento', 'prazoMedio', 'transportadoras', 'observacoes', 'calculaImposto', 'ufOrigem',
    // Canais de contato moraram na Localizacao ate 28/09 (pedido dela: um
    // lugar so, na Identificacao; a Localizacao ficou so com o endereco).
    'email', 'ddd', 'telefone', 'whatsapp', 'nomeContato', 'cargo', 'website', 'instagram',
  ],
  localizacao: ['cep', 'endereco', 'numero', 'complemento', 'bairro', 'municipio', 'uf'],
  comercial: [
    'pedidoMinimo', 'formasAtivas', 'formasExtras', 'limiteSolicitado', 'limiteAprovado', 'fretePadrao',
  ],
}

// Com virgula: converterNumeroBR le "1.500" como mil e quinhentos, e o campo
// precisa voltar num formato que ele leia igual.
function moedaParaCampo(v: number | null | undefined): string {
  return v == null ? '' : v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function formularioDe(r: Representada | undefined): FormularioRepresentada {
  const contato = r?.contatos[0]
  return {
    nome: r?.nome ?? '',
    razaoSocial: r?.razao_social ?? '',
    cnpj: r?.cnpj ?? '',
    inscricaoEstadual: r?.inscricao_estadual ?? '',
    inscricaoMunicipal: r?.inscricao_municipal ?? '',
    regimeTributario: r?.regime_tributario ?? '',
    segmento: r?.segmento ?? '',
    prazoMedio: r?.prazo_medio_entrega_dias?.toString() ?? '',
    transportadoras: (r?.transportadoras_usuais ?? []).join(', '),
    observacoes: r?.observacoes ?? '',
    // `?? false`: fornecedora de antes da 0024 le `undefined`, e ausente e "nao calcula".
    calculaImposto: r?.calcula_imposto ?? false,
    ufOrigem: r?.uf_origem ?? '',
    cep: r?.cep ?? '',
    endereco: r?.endereco ?? '',
    numero: r?.numero ?? '',
    complemento: r?.complemento ?? '',
    bairro: r?.bairro ?? '',
    municipio: r?.municipio ?? '',
    uf: r?.uf ?? '',
    email: contato?.email ?? '',
    ddd: contato?.ddd ?? '',
    telefone: contato?.telefone ?? '',
    nomeContato: contato?.nome ?? '',
    whatsapp: contato?.whatsapp ?? '',
    cargo: contato?.cargo ?? '',
    website: r?.website ?? '',
    instagram: r?.instagram ?? '',
    pedidoMinimo: moedaParaCampo(r?.pedido_minimo_valor),
    formasAtivas: [...(r?.formas_pagamento_aceitas ?? [])],
    formasExtras: [...(r?.formas_pagamento_extras ?? [])],
    limiteSolicitado: moedaParaCampo(r?.limite_credito_solicitado),
    limiteAprovado: moedaParaCampo(r?.limite_credito_aprovado),
    fretePadrao: r?.frete_padrao ?? '',
  }
}

const textoOuNulo = (s: string) => (s.trim() === '' ? null : s.trim())
const textoOuAusente = (s: string) => (s.trim() === '' ? undefined : s.trim())
const lista = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean)

export function registroDe(
  base: Representada | undefined,
  f: FormularioRepresentada,
  novo: () => Pick<RegistroBase, 'id' | 'criado_em' | 'atualizado_em' | 'apagado_em'>,
): Representada {
  const temContato = [f.email, f.ddd, f.telefone, f.whatsapp, f.nomeContato, f.cargo].some((x) => x.trim() !== '')
  // So o PRIMEIRO contato tem tela; os outros vem do banco e nao podem sumir
  // por a tela nao mostra-los.
  const outros = base?.contatos.slice(1) ?? []
  const primeiro: Contato = {
    ...base?.contatos[0],
    nome: textoOuAusente(f.nomeContato),
    ddd: textoOuAusente(f.ddd),
    telefone: textoOuAusente(f.telefone),
    email: textoOuAusente(f.email),
    whatsapp: textoOuAusente(f.whatsapp),
    cargo: textoOuAusente(f.cargo),
  }
  return {
    ...(base ?? novo()),
    nome: f.nome.trim(),
    razao_social: textoOuNulo(f.razaoSocial),
    cnpj: textoOuNulo(f.cnpj),
    inscricao_estadual: textoOuNulo(f.inscricaoEstadual),
    inscricao_municipal: textoOuNulo(f.inscricaoMunicipal),
    regime_tributario: (textoOuNulo(f.regimeTributario) as RegimeTributario | null),
    segmento: textoOuNulo(f.segmento),
    prazo_medio_entrega_dias: f.prazoMedio.trim() === '' ? null : Number(f.prazoMedio),
    transportadoras_usuais: lista(f.transportadoras),
    observacoes: textoOuNulo(f.observacoes),
    calcula_imposto: f.calculaImposto,
    uf_origem: f.calculaImposto ? textoOuNulo(f.ufOrigem) : null,
    cep: textoOuNulo(f.cep),
    endereco: textoOuNulo(f.endereco),
    numero: textoOuNulo(f.numero),
    complemento: textoOuNulo(f.complemento),
    bairro: textoOuNulo(f.bairro),
    municipio: textoOuNulo(f.municipio),
    uf: textoOuNulo(f.uf),
    website: textoOuNulo(f.website),
    instagram: textoOuNulo(f.instagram),
    contatos: temContato ? [primeiro, ...outros] : outros,
    // ⚠ converterNumeroBR, e nao Number(): "1.500,00" -- ver textoBR.ts.
    pedido_minimo_valor: converterNumeroBR(f.pedidoMinimo),
    // prazo_pagamento_padrao saiu da tela (spec 27/09) e sobrevive pelo spread da base.
    formas_pagamento_aceitas: [...f.formasAtivas],
    formas_pagamento_extras: [...f.formasExtras],
    limite_credito_solicitado: converterNumeroBR(f.limiteSolicitado),
    limite_credito_aprovado: converterNumeroBR(f.limiteAprovado),
    frete_padrao: (textoOuNulo(f.fretePadrao) as Representada['frete_padrao']),
  }
}

// Lista compara por VALOR: com formas virando string[] (spec 27/09), `!==`
// acusaria mudanca a cada render e o "Descartar?" apareceria sem motivo.
const igual = (x: unknown, y: unknown) =>
  Array.isArray(x) && Array.isArray(y) ? x.length === y.length && x.every((v, i) => v === y[i]) : x === y
export function abaMudou(aba: AbaRepresentada, a: FormularioRepresentada, b: FormularioRepresentada): boolean {
  return CAMPOS_DA_ABA[aba].some((campo) => !igual(a[campo], b[campo]))
}

export function formularioMudou(a: FormularioRepresentada, b: FormularioRepresentada): boolean {
  return (Object.keys(CAMPOS_DA_ABA) as AbaRepresentada[]).some((aba) => abaMudou(aba, a, b))
}

const DINHEIRO: ReadonlyArray<[keyof FormularioRepresentada & ('pedidoMinimo' | 'limiteSolicitado' | 'limiteAprovado'), string]> = [
  ['pedidoMinimo', 'Pedido mínimo'],
  ['limiteSolicitado', 'Limite de crédito solicitado'],
  ['limiteAprovado', 'Limite de crédito aprovado'],
]

export function erroDaAba(aba: AbaRepresentada, f: FormularioRepresentada): string | null {
  if (aba === 'identificacao') {
    if (f.nome.trim() === '') return 'Informe o nome fantasia.'
    if (f.calculaImposto && !f.ufOrigem) {
      return 'Selecione a UF de origem — obrigatória quando a representada manda alíquota.'
    }
  }
  if (aba === 'comercial') {
    for (const [campo, rotulo] of DINHEIRO) {
      if (f[campo].trim() !== '' && converterNumeroBR(f[campo]) === null) return `${rotulo}: valor inválido.`
    }
  }
  return null
}

type CampoTexto = 'endereco' | 'numero' | 'complemento' | 'bairro' | 'municipio' | 'uf' | 'cep'
  | 'nome' | 'ddd' | 'telefone' | 'email' | 'segmento'

function preencherVazios(
  f: FormularioRepresentada, pares: ReadonlyArray<readonly [CampoTexto, string | null]>,
): Partial<FormularioRepresentada> {
  const p: Partial<FormularioRepresentada> = {}
  for (const [campo, valor] of pares) if (f[campo].trim() === '' && valor) p[campo] = valor
  return p
}

// Nunca sobrescreve o que ela ja digitou (spec 17/09).
export function camposDoCnpj(f: FormularioRepresentada, e: CnpjEncontrado): Partial<FormularioRepresentada> {
  return {
    ...(f.razaoSocial.trim() === '' ? { razaoSocial: e.razaoSocial } : {}),
    ...preencherVazios(f, [
      ['endereco', e.endereco], ['numero', e.numero], ['complemento', e.complemento], ['bairro', e.bairro],
      ['municipio', e.municipio], ['uf', e.uf], ['cep', e.cep],
      ['nome', e.nomeFantasia], ['ddd', e.ddd], ['telefone', e.telefone], ['email', e.email], ['segmento', e.segmento],
    ]),
  }
}

export function camposDoCep(f: FormularioRepresentada, e: CepEncontrado): Partial<FormularioRepresentada> {
  return preencherVazios(f, [['endereco', e.endereco], ['bairro', e.bairro], ['municipio', e.municipio], ['uf', e.uf]])
}
